/**
 * FundingWindowCard Component
 * Displays a group of funding instructions for a specific funding window
 * (Ventana Matutina / Ventana Vespertina) inside a Card with a table.
 */

import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES } from '@/constants';
import type { FundingInstruction } from '@/types/funding.types';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const WINDOW_LABELS: Record<string, string> = {
  MORNING: 'Ventana Matutina',
  AFTERNOON: 'Ventana Vespertina',
};

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

function getStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'PENDING_FUNDING':
      return 'warning';
    case 'INCLUDED':
      return 'info';
    case 'CONFIRMED':
      return 'success';
    default:
      return 'default';
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'PENDING_FUNDING':
      return 'Pendiente';
    case 'INCLUDED':
      return 'Incluido';
    case 'CONFIRMED':
      return 'Confirmado';
    default:
      return status;
  }
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface FundingWindowCardProps {
  window: string;
  instructions: FundingInstruction[];
}

// ─── Component ───────────────────────────────────────────────────────────────

export function FundingWindowCard({ window: fundingWindow, instructions }: FundingWindowCardProps) {
  const navigate = useNavigate();

  const windowLabel = WINDOW_LABELS[fundingWindow] ?? fundingWindow;
  const totalAmount = instructions.reduce((sum, i) => sum + i.amount_mxn, 0);

  function handleViewDetail(fundingId: string) {
    navigate(ROUTES.FUNDING_INSTRUCTION_DETAIL.replace(':fundingId', fundingId));
  }

  return (
    <Card
      title={`${windowLabel} (${instructions.length} instrucciones)`}
      description={`Total: ${formatCurrency(totalAmount, 'MXN', 'es-MX')}`}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-border text-xs uppercase text-muted-foreground">
              <th className="px-3 py-2">ID Credito</th>
              <th className="px-3 py-2 text-right">Monto</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {(instructions ?? []).map((instruction) => (
              <tr
                key={instruction.funding_id}
                className="border-b border-border last:border-0 hover:bg-muted/50"
              >
                <td className="px-3 py-2 font-mono text-xs">{instruction.credit_id}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatCurrency(instruction.amount_mxn, 'MXN', 'es-MX')}
                </td>
                <td className="px-3 py-2">
                  <Badge variant={getStatusVariant(instruction.status)}>
                    {getStatusLabel(instruction.status)}
                  </Badge>
                </td>
                <td className="px-3 py-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleViewDetail(instruction.funding_id)}
                  >
                    Ver Detalle
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
